import { apiClient, type ApiOpts } from './client';

// ── Wire types (snake_case — Jackson global config) ──────────────────────────

export type AiWorkbenchAction = {
  type: string;
  surface: string;
  title: string;
  description: string;
  payload: Record<string, unknown>;
};

type RawAiWorkbenchAction = {
  type?: unknown;
  action?: unknown;
  surface?: unknown;
  title?: unknown;
  description?: unknown;
  payload?: unknown;
  panels?: unknown;
};

export type AiSessionRequest = {
  session_type?: string;
  provider?: string;
  model?: string;
  user_role?: string;
  desk?: string;
};

export type AiSessionResponse = {
  session_id: string;
  session_type: string;
  user_role: string;
  provider: string;
  model: string;
  created_at: string;
};

export type AiMessageRequest = {
  session_id: string;
  message: string;
  /** Optional surface context for panel-scoped AI requests. */
  context?: {
    surface: string;
    payload: unknown;
  };
};

export type AiMessageResponse = {
  session_id: string;
  response: string;
  timestamp: string;
  ui_action: AiWorkbenchAction | null;
  suggested_prompts: string[] | null;
};

type AiMessageResponseWire = Omit<AiMessageResponse, 'ui_action'> & {
  ui_action: RawAiWorkbenchAction | null;
};

function normalizeRecord(input: unknown): Record<string, unknown> {
  return input !== null && typeof input === 'object' && !Array.isArray(input)
    ? (input as Record<string, unknown>)
    : {};
}

function normalizeUiAction(input: RawAiWorkbenchAction | null): AiWorkbenchAction | null {
  if (input === null) return null;

  const type =
    typeof input.type === 'string'
      ? input.type
      : typeof input.action === 'string'
        ? input.action
        : '';

  const surface = typeof input.surface === 'string' ? input.surface : '';
  const title = typeof input.title === 'string' ? input.title : '';
  const description = typeof input.description === 'string' ? input.description : '';

  const payload = normalizeRecord(input.payload);

  // Backend may return stack items as top-level `panels`; normalize to payload.surfaces.
  if (type === 'stack_panels' && Array.isArray(input.panels)) {
    payload.surfaces = input.panels;
  }

  return {
    type,
    surface,
    title,
    description,
    payload,
  };
}

// ── API functions ─────────────────────────────────────────────────────────────

export const aiApi = {
  createSession: (
    req: AiSessionRequest,
    opts?: ApiOpts,
  ): Promise<AiSessionResponse> =>
    apiClient.post<AiSessionResponse>('/api/v1/ai/sessions', req, opts),

  sendMessage: async (
    req: AiMessageRequest,
    opts?: ApiOpts,
  ): Promise<AiMessageResponse> => {
    const res = await apiClient.post<AiMessageResponseWire>('/api/v1/ai/message', req, opts);
    return {
      ...res,
      ui_action: normalizeUiAction(res.ui_action),
    };
  },
};
